// src/hooks/usePushNotifications.js
// Push notifications hook for Boply - supports Web, iOS, and Android

import { useState, useEffect, useCallback } from 'react';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { doc, setDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../firebase';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import toast from 'react-hot-toast';

// Get this from Firebase Console > Project Settings > Cloud Messaging > Web Push certificates
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY || 'YOUR_VAPID_KEY_HERE';

export const usePushNotifications = (user, currentGroup) => {
  const [token, setToken] = useState(null);
  const [permission, setPermission] = useState('default');
  const [supported, setSupported] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isNative = Capacitor.isNativePlatform();
  const platform = isNative ? Capacitor.getPlatform() : 'web';

  // Save token to Firestore
  const saveTokenToFirestore = useCallback(async (pushToken) => {
    if (!user || !pushToken) return;

    try {
      // Save to user document
      const userRef = doc(db, 'users', user.uid);
      await setDoc(userRef, {
        pushTokens: arrayUnion({
          token: pushToken,
          platform,
          updatedAt: new Date().toISOString(),
        }),
        notificationsEnabled: true,
        lastTokenUpdate: new Date().toISOString(),
      }, { merge: true });

      // Also save to group if user is in one
      if (currentGroup) {
        const groupTokenRef = doc(db, 'groups', currentGroup.id, 'pushTokens', user.uid);
        await setDoc(groupTokenRef, {
          token: pushToken,
          platform,
          userId: user.uid,
          email: user.email,
          displayName: user.displayName || user.email?.split('@')[0],
          updatedAt: new Date().toISOString(),
        });
      }

      console.log('✅ Push token saved to Firestore');
    } catch (err) {
      console.error('Error saving push token:', err);
    }
  }, [user, currentGroup, platform]);

  // Handle foreground notification
  const handleForegroundNotification = useCallback((payload) => {
    console.log('📬 Foreground notification:', payload);
    
    const title = payload.title || payload.notification?.title || 'Boply';
    const body = payload.body || payload.notification?.body || '';
    
    // Show toast notification
    toast(body, {
      icon: getNotificationIcon(payload.data?.type),
      duration: 5000,
    });
  }, []);

  // Get icon based on notification type
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'new_poll': return '🗳️';
      case 'show_added': return '🎸';
      case 'event_added': return '📅';
      case 'show_reminder': return '🎸';
      case 'show_tomorrow': return '📅';
      case 'band_invite': return '🎵';
      case 'low_inventory': return '⚠️';
      default: return '🔔';
    }
  };

  // Handle notification tap
  const handleNotificationTap = useCallback((notification) => {
    console.log('👆 Notification tapped:', notification);
    
    const data = notification.notification?.data || notification.data || {};
    
    // Navigate based on notification type
    switch (data.type) {
      case 'new_poll':
        window.location.href = '/#/polls';
        break;
      case 'show_added':
      case 'event_added':
      case 'show_reminder':
      case 'show_tomorrow':
        window.location.href = '/#/calendar';
        break;
      case 'band_invite':
        window.location.href = '/#/settings';
        break;
      case 'low_inventory':
        window.location.href = '/#/inventory';
        break;
      default:
        window.location.href = '/#/dashboard';
    }
  }, []);

  // ============================================
  // NATIVE PUSH (iOS/Android)
  // ============================================
  const initNativePush = useCallback(async () => {
    try {
      const permResult = await PushNotifications.checkPermissions();
      setPermission(permResult.receive);
      setSupported(true);

      if (permResult.receive === 'granted') {
        await PushNotifications.register();
      }

      // Listen for registration
      PushNotifications.addListener('registration', async (tokenData) => {
        console.log('📱 Native push token:', tokenData.value);
        setToken(tokenData.value);
        await saveTokenToFirestore(tokenData.value);
      });

      // Listen for errors
      PushNotifications.addListener('registrationError', (err) => {
        console.error('❌ Push registration error:', err);
        setError(err.error);
      });

      // Listen for foreground notifications
      PushNotifications.addListener('pushNotificationReceived', (notification) => {
        handleForegroundNotification(notification);
      });

      // Listen for notification taps
      PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        handleNotificationTap(action);
      });

    } catch (err) {
      console.error('Native push init error:', err);
      setSupported(false);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [saveTokenToFirestore, handleForegroundNotification, handleNotificationTap]);

  const requestNativePermission = useCallback(async () => {
    try {
      setLoading(true);
      const result = await PushNotifications.requestPermissions();
      setPermission(result.receive);

      if (result.receive === 'granted') {
        await PushNotifications.register();
        return true;
      }
      return false;
    } catch (err) {
      console.error('Request permission error:', err);
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // ============================================
  // WEB PUSH
  // ============================================
  const initWebPush = useCallback(async () => {
    try {
      if (!('Notification' in window)) {
        console.log('Browser does not support notifications');
        setSupported(false);
        setLoading(false);
        return;
      }

      if (!('serviceWorker' in navigator)) {
        console.log('Browser does not support service workers');
        setSupported(false);
        setLoading(false);
        return;
      }

      setSupported(true);
      setPermission(Notification.permission);

      if (Notification.permission === 'granted') {
        await registerWebPush();
      }
    } catch (err) {
      console.error('Web push init error:', err);
      setSupported(false);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const registerWebPush = useCallback(async () => {
    try {
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      console.log('Service worker registered:', registration);

      const messaging = getMessaging();
      
      const fcmToken = await getToken(messaging, { 
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: registration,
      });

      if (fcmToken) {
        console.log('🌐 FCM Token:', fcmToken);
        setToken(fcmToken);
        await saveTokenToFirestore(fcmToken);
      }

      onMessage(messaging, (payload) => {
        handleForegroundNotification(payload);
      });

    } catch (err) {
      console.error('Web push registration error:', err);
      setError(err.message);
    }
  }, [saveTokenToFirestore, handleForegroundNotification]);

  const requestWebPermission = useCallback(async () => {
    try {
      setLoading(true);
      const permission = await Notification.requestPermission();
      setPermission(permission);

      if (permission === 'granted') {
        await registerWebPush();
        return true;
      }
      return false;
    } catch (err) {
      console.error('Request permission error:', err);
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, [registerWebPush]);

  // ============================================
  // UNIFIED API
  // ============================================
  
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    if (isNative) {
      initNativePush();
    } else {
      initWebPush();
    }
  }, [user, isNative, initNativePush, initWebPush]);

  const requestPermission = useCallback(async () => {
    if (isNative) {
      return await requestNativePermission();
    } else {
      return await requestWebPermission();
    }
  }, [isNative, requestNativePermission, requestWebPermission]);

  const removeToken = useCallback(async () => {
    if (!user || !token) return;

    try {
      const userRef = doc(db, 'users', user.uid);
      await setDoc(userRef, {
        pushTokens: arrayRemove({ token, platform }),
        notificationsEnabled: false,
      }, { merge: true });

      if (currentGroup) {
        const groupTokenRef = doc(db, 'groups', currentGroup.id, 'pushTokens', user.uid);
        await setDoc(groupTokenRef, { token: null }, { merge: true });
      }

      setToken(null);
      console.log('Push token removed');
    } catch (err) {
      console.error('Error removing token:', err);
    }
  }, [user, currentGroup, token, platform]);

  return {
    token,
    permission,
    supported,
    loading,
    error,
    platform,
    isNative,
    isEnabled: permission === 'granted' && !!token,
    canRequest: permission !== 'denied' && supported,
    requestPermission,
    removeToken,
  };
};

export default usePushNotifications;